import React, { useEffect, useState } from 'react';
import { motion, useMotionValue } from 'framer-motion';

const CursorEffect: React.FC = () => {
  const cursorX = useMotionValue(0);
  const cursorY = useMotionValue(0);

  const [isVisible, setIsVisible] = useState(false);
  const [isHovering, setIsHovering] = useState(false);
  const [isClicking, setIsClicking] = useState(false);

  useEffect(() => {
    const moveCursor = (e: MouseEvent) => {
      // Direct update for instant cursor tracking - no delay
      cursorX.set(e.clientX);
      cursorY.set(e.clientY);
      if (!isVisible) setIsVisible(true);
    };

    const handleMouseDown = () => setIsClicking(true);
    const handleMouseUp = () => setIsClicking(false);

    const handleMouseOver = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const isInteractive =
        target.tagName === 'A' ||
        target.tagName === 'BUTTON' ||
        target.closest('a') !== null ||
        target.closest('button') !== null ||
        target.classList.contains('cursor-pointer') ||
        target.closest('.cursor-pointer') !== null;

      setIsHovering(isInteractive);
    };

    const handleMouseLeave = () => {
      setIsVisible(false);
      setIsHovering(false);
    };

    window.addEventListener('mousemove', moveCursor, { passive: true });
    window.addEventListener('mousedown', handleMouseDown);
    window.addEventListener('mouseup', handleMouseUp);
    document.addEventListener('mouseover', handleMouseOver, { passive: true });
    window.addEventListener('mouseleave', handleMouseLeave);

    return () => {
      window.removeEventListener('mousemove', moveCursor);
      window.removeEventListener('mousedown', handleMouseDown);
      window.removeEventListener('mouseup', handleMouseUp);
      document.removeEventListener('mouseover', handleMouseOver);
      window.removeEventListener('mouseleave', handleMouseLeave);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cursorX, cursorY]);

  return (
    <>
      {/* Hide default cursor only on desktop */}
      <style>{`
        @media (min-width: 768px) {
          * {
            cursor: none !important;
          }
        }
      `}</style>

      {/* Main cursor - smooth liquid morphing effect */}
      <motion.div
        className="pointer-events-none fixed z-[200] hidden md:block"
        style={{
          x: cursorX,
          y: cursorY,
          translateX: "-50%",
          translateY: "-50%",
        }}
      >
        <motion.div
          className="relative"
          animate={{
            scale: isClicking ? 0.8 : isHovering ? 1.5 : 1,
          }}
          transition={{ type: "spring", stiffness: 400, damping: 17 }}
        >
          {/* Beautiful gradient orb */}
          <motion.div
            className="relative h-6 w-6"
            animate={{
              rotate: isHovering ? 90 : 0,
            }}
            transition={{ duration: 0.3 }}
          >
            {/* Outer glow */}
            <div className="absolute inset-0 rounded-full bg-gradient-to-br from-matcha-400/40 via-matcha-500/30 to-emerald-400/20 blur-md" />

            {/* Inner core with gradient */}
            <motion.div
              className="absolute inset-1 rounded-full bg-gradient-to-br from-matcha-400 via-matcha-500 to-matcha-600"
              animate={{
                opacity: isVisible ? 0.9 : 0,
              }}
              transition={{ duration: 0.2 }}
            />

            {/* White center dot */}
            <motion.div
              className="absolute inset-[10px] rounded-full bg-white"
              animate={{
                scale: isHovering ? 0.5 : 0.3,
              }}
              transition={{
                type: "spring",
                stiffness: 300,
                damping: 30
              }}
            />
          </motion.div>
        </motion.div>
      </motion.div>

      {/* Simplified trailing dots for better performance */}
      {[0, 1, 2].map((index) => (
        <motion.div
          key={index}
          className="pointer-events-none fixed z-[199] hidden md:block"
          style={{
            x: cursorX,
            y: cursorY,
            translateX: "-50%",
            translateY: "-50%",
          }}
          initial={{ opacity: 0 }}
          animate={{
            opacity: isVisible ? 0.3 - index * 0.08 : 0,
          }}
          transition={{
            opacity: { duration: 0.2 },
            delay: index * 0.03,
          }}
        >
          <motion.div
            animate={{
              x: isHovering ? (index === 0 ? -15 : index === 1 ? 15 : 0) : 0,
              y: isHovering ? (index === 0 ? 0 : index === 1 ? 0 : 15) : 0,
              scale: isHovering ? 1.2 : 1,
            }}
            transition={{
              type: "spring",
              stiffness: 200,
              damping: 25,
            }}
          >
            <div
              className={`h-1.5 w-1.5 rounded-full ${
                index === 0 ? 'bg-matcha-400' :
                index === 1 ? 'bg-emerald-400' :
                'bg-matcha-500'
              }`}
            />
          </motion.div>
        </motion.div>
      ))}

      {/* Ripple effect on click */}
      <motion.div
        className="pointer-events-none fixed z-[197] hidden md:block"
        style={{
          x: cursorX,
          y: cursorY,
          translateX: "-50%",
          translateY: "-50%",
        }}
      >
        <motion.div
          className="h-10 w-10 rounded-full border-2 border-matcha-400/50"
          initial={{ scale: 0, opacity: 0 }}
          animate={{
            scale: isClicking ? [0, 2] : 0,
            opacity: isClicking ? [1, 0] : 0,
          }}
          transition={{ duration: 0.5 }}
        />
      </motion.div>

      {/* Hover ring animation - simplified */}
      <motion.div
        className="pointer-events-none fixed z-[196] hidden md:block"
        style={{
          x: cursorX,
          y: cursorY,
          translateX: "-50%",
          translateY: "-50%",
        }}
      >
        <motion.div
          className="h-12 w-12 rounded-full border border-matcha-400/30"
          animate={{
            opacity: isHovering ? 0.5 : 0,
            scale: isHovering ? 1 : 0.8,
          }}
          transition={{
            type: "spring",
            stiffness: 300,
            damping: 30
          }}
        />
      </motion.div>

      {/* Simplified trail effect */}
      <motion.div
        className="pointer-events-none fixed z-[195] hidden md:block"
        style={{
          x: cursorX,
          y: cursorY,
          translateX: "-50%",
          translateY: "-50%",
        }}
        initial={{ opacity: 0 }}
        animate={{
          opacity: isVisible ? 0.15 : 0,
        }}
        transition={{
          opacity: { duration: 0.3 },
        }}
      >
        <div className="h-10 w-10 rounded-full bg-gradient-to-br from-matcha-400/10 to-transparent blur-xl" />
      </motion.div>
    </>
  );
};

export default CursorEffect;